/**
 * TaskManager Page
 * Integrates PlatformServices/TaskManager component
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TASK_MANAGER_SHELL_SUBTITLE } from '@saas-foundation/shared-frontend/workqueue/rcmWorkDomain';
import { Clock, Trash2, Plus, RefreshCw, CheckCircle, XCircle, AlertCircle, Calendar, User, Mail, MessageSquare, Webhook, Users, UserPlus, Settings, Shield, DollarSign, FileText, X } from 'lucide-react';

const TaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showAgentsPanel, setShowAgentsPanel] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    task_type: 'email',
    scheduled_time: '',
    payload: '{}',
    callback_url: ''
  });
  const [assignmentData, setAssignmentData] = useState({
    task_id: null,
    agent_id: '',
    priority: 'normal',
    instructions: ''
  });

  // API base URL — shell proxy on remote hosts; :8051 on localhost
  const getApiBase = () => {
    const envUrl = process.env.REACT_APP_TASK_MANAGER_API_URL || process.env.REACT_APP_API_URL;
    if (envUrl && /^https?:\/\//i.test(String(envUrl).trim())) {
      const u = String(envUrl).trim().replace(/\/$/, '');
      return u.endsWith('/api') ? u : `${u}/api`;
    }
    if (typeof window !== 'undefined' && window.location) {
      const { hostname, port } = window.location;
      const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
      const useDefaultPorts = process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true';
      const shellProxy =
        !useDefaultPorts &&
        (!isLocal || port === '3160' || port === '80' || port === '443' || port === '');
      if (shellProxy) {
        return '/rcm-api/task-manager/api';
      }
      return (() => { const __h = hostname; return (__h && __h !== 'localhost' && __h !== '127.0.0.1' && !/^(\d{1,3}\.){3}\d{1,3}$/.test(__h)) ? '/api' : `${window.location.protocol}//${hostname}:8051/api`; })();
    }
    return 'http://localhost:8051/api';
  };
  const API_BASE = getApiBase();

  // Helper function to get organization_id params
  const getOrgParams = () => {
    const organizationId = localStorage.getItem('selectedOrganizationId') ||
                           localStorage.getItem('organizationId');
    return organizationId ? { organization_id: organizationId } : {};
  };

  const getAuthHeaders = () => {
    const authToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  };

  // Get mock RCM tasks for demo purposes
  const getMockRCMTasks = () => {
    const now = new Date();
    return [
      {
        id: 'task-rcm-001',
        name: 'Review Denial - CLM-001',
        description: 'Claim CLM-001 for John Doe ($500.00) was denied. Review denial reason and prepare appeal if applicable.',
        task_type: 'denial_review',
        status: 'pending',
        priority: 'high',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 30).toISOString(), // 30 minutes from now
        payload: JSON.stringify({
          claim_id: 'CLM-001',
          patient_name: 'John Doe',
          amount: 500.00,
          denial_code: 'OA-50',
          denial_reason: 'Prior authorization required',
          appeal_deadline: new Date(now.getTime() + 1000 * 60 * 60 * 24 * 7).toISOString()
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 15).toISOString(),
        metadata: { service: 'DenialManagement', category: 'denials' }
      },
      {
        id: 'task-rcm-002',
        name: 'Submit Appeal - CLM-002',
        description: 'Submit appeal for claim CLM-002 (Jane Smith, $750.00) to Blue Cross Blue Shield.',
        task_type: 'appeal_submission',
        status: 'in_progress',
        priority: 'high',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 60).toISOString(), // 1 hour from now
        payload: JSON.stringify({
          claim_id: 'CLM-002',
          patient_name: 'Jane Smith',
          amount: 750.00,
          payer: 'Blue Cross Blue Shield',
          appeal_type: 'authorization_required'
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 45).toISOString(),
        metadata: { service: 'DenialManagement', category: 'denials' }
      },
      {
        id: 'task-rcm-003',
        name: 'Process Claims Batch',
        description: 'Process batch of 15 claims that are ready for submission to payers.',
        task_type: 'claim_processing',
        status: 'pending',
        priority: 'normal',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 60 * 2).toISOString(), // 2 hours from now
        payload: JSON.stringify({
          batch_id: 'BATCH-2024-001',
          claim_count: 15,
          total_amount: 12500.00
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 3).toISOString(),
        metadata: { service: 'ClaimsProcessing', category: 'claims' }
      },
      {
        id: 'task-rcm-004',
        name: 'Post Payment - CLM-045',
        description: 'Post payment of $2,450.00 received for claim CLM-045 (Robert Johnson).',
        task_type: 'payment_posting',
        status: 'pending',
        priority: 'normal',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 30).toISOString(),
        payload: JSON.stringify({
          claim_id: 'CLM-045',
          patient_name: 'Robert Johnson',
          payment_amount: 2450.00,
          payment_date: new Date().toISOString()
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 4).toISOString(),
        metadata: { service: 'ARPosting', category: 'ar-posting' }
      },
      {
        id: 'task-rcm-005',
        name: 'Verify Eligibility - PAT-007',
        description: 'Verify insurance eligibility for patient Michael Brown. Previous check failed.',
        task_type: 'eligibility_verification',
        status: 'pending',
        priority: 'medium',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 60 * 1.5).toISOString(),
        payload: JSON.stringify({
          patient_id: 'PAT-007',
          patient_name: 'Michael Brown',
          insurance_provider: 'Aetna',
          previous_status: 'failed'
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 6).toISOString(),
        metadata: { service: 'InsuranceVerification', category: 'insurance' }
      },
      {
        id: 'task-rcm-006',
        name: 'Review Prior Auth Request',
        description: 'Review prior authorization request PA-0123 for Sarah Wilson. Status: Pending approval.',
        task_type: 'prior_auth_review',
        status: 'in_progress',
        priority: 'medium',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 45).toISOString(),
        payload: JSON.stringify({
          auth_id: 'PA-0123',
          patient_name: 'Sarah Wilson',
          procedure: 'MRI Scan',
          status: 'pending_approval'
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 5).toISOString(),
        metadata: { service: 'PriorAuthorization', category: 'prior-auth' }
      },
      {
        id: 'task-rcm-007',
        name: 'Capture Charges - Today\'s Encounters',
        description: 'Capture and post charges for 25 encounters from today\'s appointments.',
        task_type: 'charge_capture',
        status: 'pending',
        priority: 'normal',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 60 * 3).toISOString(),
        payload: JSON.stringify({
          encounter_count: 25,
          date: new Date().toISOString().split('T')[0]
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 14).toISOString(),
        metadata: { service: 'ChargeCapture', category: 'charges' }
      },
      {
        id: 'task-rcm-008',
        name: 'Resolve EDI Transaction Error',
        description: 'Resolve EDI 837 transaction error for claim CLM-089. Error: Invalid payer ID.',
        task_type: 'edi_error_resolution',
        status: 'pending',
        priority: 'high',
        scheduled_time: new Date(now.getTime() + 1000 * 60 * 60 * 1).toISOString(),
        payload: JSON.stringify({
          claim_id: 'CLM-089',
          transaction_type: '837',
          error: 'Invalid payer ID',
          payer_id: 'UNKNOWN'
        }),
        created_at: new Date(now.getTime() - 1000 * 60 * 60 * 12).toISOString(),
        metadata: { service: 'EdiPlatform', category: 'clearinghouse' }
      }
    ];
  };

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE}/tasks/`, { params: getOrgParams(), headers: getAuthHeaders() });
      // If API returns empty array or error, use mock RCM tasks
      if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
        console.log('[TaskManager] Using mock RCM tasks for demo');
        setTasks(getMockRCMTasks());
      } else {
        setTasks(response.data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      console.log('[TaskManager] API unavailable, using mock RCM tasks for demo');
      // Use mock tasks when API is unavailable
      setTasks(getMockRCMTasks());
      setError(null); // Don't show error - use mock data instead
    } finally {
      setLoading(false);
    }
  };

  // Fetch executions for selected task
  const fetchExecutions = async (taskId) => {
    try {
      const response = await axios.get(`${API_BASE}/tasks/${taskId}/executions`, { params: getOrgParams(), headers: getAuthHeaders() });
      setExecutions(response.data);
    } catch (error) {
      console.error('Error fetching executions:', error);
      setError('Failed to fetch execution history.');
    }
  };

  // Fetch agents
  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_BASE}/agents/`, { headers: getAuthHeaders() });
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setError('Failed to fetch agents.');
    }
  };

  // Fetch task assignments
  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API_BASE}/agents/assignments`, { headers: getAuthHeaders() });
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  // Create new task
  const createTask = async () => {
    try {
      setCreating(true);
      setError(null);
      let payload;
      try {
        payload = JSON.parse(newTask.payload);
      } catch (e) {
        throw new Error('Invalid JSON in payload field');
      }

      const taskData = {
        ...newTask,
        payload,
      };

      await axios.post(`${API_BASE}/tasks/`, taskData, { params: getOrgParams(), headers: getAuthHeaders() });
      setSuccessMessage('Task created successfully!');
      setShowCreateForm(false);
      setNewTask({
        name: '',
        description: '',
        task_type: 'email',
        scheduled_time: '',
        payload: '{}',
        callback_url: ''
      });
      fetchTasks();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error creating task:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to create task.');
    } finally {
      setCreating(false);
    }
  };

  // Assign task to agent
  const assignTask = async () => {
    try {
      setAssigning(true);
      setError(null);
      await axios.post(`${API_BASE}/agents/assignments`, assignmentData, { params: getOrgParams(), headers: getAuthHeaders() });
      setSuccessMessage('Task assigned successfully!');
      setShowAssignForm(false);
      setAssignmentData({
        task_id: null,
        agent_id: '',
        priority: 'normal',
        instructions: ''
      });
      fetchAssignments();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error assigning task:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to assign task.');
    } finally {
      setAssigning(false);
    }
  };

  // Execute task immediately
  const executeTask = async (taskId) => {
    try {
      setError(null);
      await axios.post(`${API_BASE}/tasks/${taskId}/execute`, {}, { params: getOrgParams(), headers: getAuthHeaders() });
      setSuccessMessage('Task execution started!');
      fetchTasks();
      if (selectedTask?.id === taskId) {
        fetchExecutions(taskId);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error executing task:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to execute task.');
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      setError(null);
      await axios.delete(`${API_BASE}/tasks/${taskId}`, { params: getOrgParams(), headers: getAuthHeaders() });
      setSuccessMessage('Task deleted successfully!');
      fetchTasks();
      if (selectedTask?.id === taskId) {
        setSelectedTask(null);
        setExecutions([]);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting task:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to delete task.');
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchAgents();
    fetchAssignments();
  }, []);

  useEffect(() => {
    if (selectedTask) {
      fetchExecutions(selectedTask.id);
    }
  }, [selectedTask]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'running': return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'webhook': return <Webhook className="w-4 h-4" />;
      case 'denial_review': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'appeal_submission': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'claim_processing': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'payment_posting': return <DollarSign className="w-4 h-4 text-green-500" />;
      case 'eligibility_verification': return <Shield className="w-4 h-4 text-blue-500" />;
      case 'prior_auth_review': return <CheckCircle className="w-4 h-4 text-yellow-500" />;
      case 'charge_capture': return <DollarSign className="w-4 h-4 text-purple-500" />;
      case 'edi_error_resolution': return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Detect if running in iframe
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

  return (
    <div className={isInIframe ? "w-full p-2 bg-gray-50 min-h-screen" : "w-full p-4 bg-gray-50 min-h-screen"}>
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Task Manager</h1>
          <p className="mt-2 text-gray-600 max-w-3xl">{TASK_MANAGER_SHELL_SUBTITLE}</p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
                ×
              </button>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-700">{successMessage}</span>
              <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-600 hover:text-green-800">
                ×
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex gap-3">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </button>
          <button
            onClick={() => setShowAgentsPanel(!showAgentsPanel)}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Users className="w-4 h-4 mr-2" />
            {showAgentsPanel ? 'Hide' : 'Show'} Agents
          </button>
          <button
            onClick={fetchTasks}
            className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>

        {/* Main Content */}
        <div className={`grid grid-cols-1 lg:grid-cols-3 ${isInIframe ? "gap-2" : "gap-6"}`}>
          {/* Tasks List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className={isInIframe ? "p-2 border-b border-gray-200" : "p-4 border-b border-gray-200"}>
                <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
              </div>
              <div className={isInIframe ? "p-2" : "p-4"}>
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
                    <p className="text-gray-600">Loading tasks...</p>
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">No tasks found. Create your first task!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <div
                        key={task.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedTask?.id === task.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedTask(task)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getTaskTypeIcon(task.task_type)}
                              <h3 className="font-semibold text-gray-900">{task.name}</h3>
                              {getStatusIcon(task.status)}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {task.scheduled_time ? new Date(task.scheduled_time).toLocaleString() : 'Immediate'}
                              </span>
                              <span className="capitalize">{task.task_type}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                executeTask(task.id);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Execute Now"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteTask(task.id);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Task Details & Executions */}
          <div className="space-y-6">
            {selectedTask && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Task Details</h2>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Name</label>
                    <p className="text-gray-900">{selectedTask.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Description</label>
                    <p className="text-gray-900">{selectedTask.description}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Type</label>
                    <p className="text-gray-900 capitalize">{selectedTask.task_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(selectedTask.status)}
                      <span className="text-gray-900 capitalize">{selectedTask.status}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Scheduled Time</label>
                    <p className="text-gray-900">
                      {selectedTask.scheduled_time
                        ? new Date(selectedTask.scheduled_time).toLocaleString()
                        : 'Immediate'}
                    </p>
                  </div>
                  {selectedTask.callback_url && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Callback URL</label>
                      <p className="text-gray-900 text-sm break-all">{selectedTask.callback_url}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Execution History */}
            {selectedTask && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Execution History</h2>
                </div>
                <div className="p-4">
                  {executions.length === 0 ? (
                    <p className="text-sm text-gray-500">No executions yet</p>
                  ) : (
                    <div className="space-y-3">
                      {executions.map((execution) => (
                        <div key={execution.id} className="p-3 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(execution.status)}
                              <span className="text-sm font-medium capitalize">{execution.status}</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(execution.executed_at).toLocaleString()}
                            </span>
                          </div>
                          {execution.error_message && (
                            <p className="text-xs text-red-600 mt-1">{execution.error_message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Task Modal */}
        {showCreateForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateForm(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                <h2 className="text-2xl font-bold">Create New Task</h2>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Task Name</label>
                  <input
                    type="text"
                    value={newTask.name}
                    onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Send Appointment Reminder"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                    placeholder="Task description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                  <select
                    value={newTask.task_type}
                    onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="webhook">Webhook</option>
                    <option value="reminder">Reminder</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time (optional)</label>
                  <input
                    type="datetime-local"
                    value={newTask.scheduled_time}
                    onChange={(e) => setNewTask({ ...newTask, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payload (JSON)</label>
                  <textarea
                    value={newTask.payload}
                    onChange={(e) => setNewTask({ ...newTask, payload: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    rows="5"
                    placeholder='{"to_email": "user@example.com", "subject": "Test", "body": "Message"}'
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Callback URL (optional)</label>
                  <input
                    type="url"
                    value={newTask.callback_url}
                    onChange={(e) => setNewTask({ ...newTask, callback_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="https://your-app.com/webhook"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end shrink-0 bg-white">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={createTask}
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskManager;

